import {Trans} from '@lingui/macro';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {OkCancelDialog} from '../../../components/common/dialogs/okCancelDialog';
import {useLinkNavigation} from '../../../components/common/navigation/useLinkNavigation';
import {useAppSelector} from '../../../helpers/reduxHelpers';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {LocalApplicationModel, UnsavedLocalApplicationModel} from '../../../models/api/localApplicationModel';
import {buildSettingsPath} from '../../../models/url/settingsUrlHelpers';
import {
  SettingsApplicationComponentsEnum,
  SettingsApplicationsDetailsUrl,
  SettingsResourcePagesEnum,
  SettingsRootPagesEnum,
} from '../../../models/url/settingsUrlModel';
import {getCompany} from '../../../store/data/company/companySelectors';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {useFetchInstalledApplications} from '../../fetchers/installedApplications';
import {InstalledApplicationMutationsEnum} from '../../fetchers/installedApplicationsMutations';
import {ApplicationEditorSkeleton} from './applicationEditorSkeleton';
import {ApplicationEditorWrapper} from './applicationEditorWrapper';

/*
 * Props.
 */

interface SettingsApplicationDetailsProps {
  panelId: SettingsPanelId;
  application?: LocalApplicationModel;
  installedApplication?: ApplicationModel;
  url: SettingsApplicationsDetailsUrl;
  setPageTitle: (title: React.ReactNode) => void;
  setPageActions: (title: React.ReactNode) => void;
  onEditApplication: (
    app: UnsavedLocalApplicationModel,
    applicationId: number,
    applicationUrl: string,
  ) => void;
  onCreateApplication: (app: UnsavedLocalApplicationModel) => void;
}

/*
 * Style.
 */

const StyledDetailsWrapperDiv = styled.div`
  padding: 0 48px;
`;

const StyledDescriptionDiv = styled.div`
  font-size: ${fontSizes.large};
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.medium};
  margin-bottom: 8px;
`;

const StyledOkCancelDialog = styled(OkCancelDialog)`
  margin: 0 16px;
`;

/*
 * Component.
 */

export const SettingsApplicationDetails: FC<SettingsApplicationDetailsProps> = (props) => {
  const {
    panelId,
    application,
    installedApplication,
    url,
    setPageTitle,
    setPageActions,
    onEditApplication,
    onCreateApplication,
  } = props;
  const {resourcePage} = url;

  const company = useAppSelector(getCompany);

  const [isShowingUninstallConfirmationDialog, setIsShowingUninstallConfirmationDialog] = useState(false);

  const {mutate: installedApplicationMutate} = useFetchInstalledApplications('');

  const {navigateToLink} = useLinkNavigation();
  const navigateToApplicationsList = useCallback(
    () =>
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.LIST,
          resourcePage: SettingsResourcePagesEnum.OVERVIEW,
          searchQuery: undefined,
          applicationId: undefined,
        }),
      ),
    [navigateToLink],
  );
  const navigateToEditPage = useCallback(
    (applicationId: number) =>
      navigateToLink(
        buildSettingsPath({
          rootPage: SettingsRootPagesEnum.APPLICATIONS,
          componentPage: SettingsApplicationComponentsEnum.DETAILS,
          resourcePage: SettingsResourcePagesEnum.EDIT,
          applicationId,
          searchQuery: undefined,
        }),
      ),
    [navigateToLink],
  );

  useEffect(() => {
    setPageTitle(computePageTitle(url, installedApplication));
    const shouldShowEditButton = Boolean(
      application && url.resourcePage === SettingsResourcePagesEnum.OVERVIEW,
    );
    const shouldShowUninstallButton = Boolean(
      !application && url.resourcePage === SettingsResourcePagesEnum.OVERVIEW,
    );
    setPageActions(
      renderPageAction(
        shouldShowEditButton,
        shouldShowUninstallButton,
        () => {
          // The edit page uses the application container, which needs an installation ID.
          // It will then fetch the local application as well.
          if (installedApplication) {
            navigateToEditPage(installedApplication.id);
          }
        },
        () => setIsShowingUninstallConfirmationDialog(true),
      ),
    );
  }, [application, installedApplication, url, setPageTitle, setPageActions, navigateToEditPage]);

  if (resourcePage === SettingsResourcePagesEnum.OVERVIEW) {
    return (
      <StyledDetailsWrapperDiv>
        <StyledDescriptionDiv>
          <Trans id="applications-details-application-title">
            A local application represents a 3rd party service that integrates with Front (eg: Jira). It is
            owned by a company and can be made publicly available for other companies to install it.
          </Trans>
        </StyledDescriptionDiv>
        {isShowingUninstallConfirmationDialog && (
          <StyledOkCancelDialog
            title={<Trans id="applications-details-application-uninstall-title">Uninstall Application</Trans>}
            onRequestClose={() => setIsShowingUninstallConfirmationDialog(false)}
            onOkClick={() => {
              setIsShowingUninstallConfirmationDialog(false);
              if (!installedApplication) {
                return;
              }

              installedApplicationMutate({
                type: InstalledApplicationMutationsEnum.DELETE,
                payload: {
                  company,
                  applicationIds: [installedApplication.id],
                },
              });

              navigateToApplicationsList();
            }}
            okButtonLabel={<Trans id="applications-details-application-uninstall">Uninstall</Trans>}
            okButtonVariant={ButtonVariantsEnum.DANGER}
          >
            <Trans id="applications-details-application-uninstall-description">
              Uninstalling the application will remove all previewers and autolinkers. Any previewed linked or
              auto-linked link will remain.
            </Trans>
          </StyledOkCancelDialog>
        )}
      </StyledDetailsWrapperDiv>
    );
  }

  if (resourcePage === SettingsResourcePagesEnum.NEW || resourcePage === SettingsResourcePagesEnum.EDIT) {
    if (resourcePage === SettingsResourcePagesEnum.EDIT && !application) {
      return <ApplicationEditorSkeleton />;
    }

    return (
      <ApplicationEditorWrapper
        panelId={panelId}
        application={application}
        installedApplication={installedApplication}
        onSaveApplication={(unsavedApplication) => {
          if (application) {
            onEditApplication(unsavedApplication, application.id, application.url);
          } else {
            onCreateApplication(unsavedApplication);
          }
        }}
      />
    );
  }

  return null;
};

/*
 * Helpers.
 */

function computePageTitle(url: SettingsApplicationsDetailsUrl, installedApplication?: ApplicationModel) {
  if (url.resourcePage === SettingsResourcePagesEnum.NEW) {
    return <Trans id="applications-details-create-title">Create application</Trans>;
  }
  if (url.resourcePage === SettingsResourcePagesEnum.EDIT) {
    return <Trans id="applications-details-edit-title">Edit application {installedApplication?.name}</Trans>;
  }
  return installedApplication?.name;
}

function renderPageAction(
  shouldShowEditButton: boolean,
  shouldShowUninstallButton: boolean,
  onEditClick: Handler,
  onUninstallClick: Handler,
) {
  return (
    <>
      {shouldShowEditButton && (
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onEditClick}>
          <Trans id="applications-details-edit-button">Edit application</Trans>
        </Button>
      )}
      {shouldShowUninstallButton && (
        <Button variant={ButtonVariantsEnum.SECONDARY_DANGER} onClick={onUninstallClick}>
          <Trans id="applications-details-uninstall-button">Uninstall application</Trans>
        </Button>
      )}
    </>
  );
}
